export interface MergeOptions {
    clone?: boolean;
    customMerge?: (key: string, options?: MergeOptions) => ((x: any, y: any) => any) | undefined;
    arrayMerge?(target: any[], source: any[], options?: MergeOptions): any[];
    isMergeableObject?(value: object): boolean;
}

/**
 * Deepmerge target and source object
 * @param target Target object
 * @param source Srouce object
 * @param options
 */
export function deepmerge(target: any, source: any, options?: MergeOptions) {
    options = options || {};
    options.arrayMerge = options.arrayMerge || defaultArrayMerge;
    options.isMergeableObject = options.isMergeableObject || isMergeableObject;

    const sourceIsArray = Array.isArray(source);
    const targetIsArray = Array.isArray(target);
    const sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;

    if (!sourceAndTargetTypesMatch) {
        return cloneUnlessOtherwiseSpecified(source, options)
    } else if (sourceIsArray) {
        return options.arrayMerge(target, source, options)
    } else {
        return mergeObject(target, source, options)
    }
}

export function mergeObject(target: any, source: any, options: MergeOptions) {
    const destination = {};
    if (options.isMergeableObject(target)) {
        getKeys(target).forEach(function(key) {
            destination[key] = cloneUnlessOtherwiseSpecified(target[key], options)
        })
    }
    getKeys(source).forEach(function(key) {
        if (!options.isMergeableObject(source[key]) || !target[key]) {
            destination[key] = cloneUnlessOtherwiseSpecified(source[key], options)
        } else {
            destination[key] = getMergeFunction(key, options)(target[key], source[key], options)
        }
    });
    return destination
}

// custom merge strategy
// doest not concat arrays like default strategy. Simply overrides
export const overwriteMerge = (destinationArray, sourceArray, options) => sourceArray;

/***************** methods used internally by the exported functions above *********************/

export interface MergeOptions {
    clone?: boolean;
    customMerge?: (key: string, options?: MergeOptions) => ((x: any, y: any) => any) | undefined;
    arrayMerge?(target: any[], source: any[], options?: MergeOptions): any[];
    isMergeableObject?(value: object): boolean;
}

export function isMergeableObject(val: any): boolean {
    const nonNullObject = val && typeof val === 'object';

    return nonNullObject
      && Object.prototype.toString.call(val) !== '[object RegExp]'
      && Object.prototype.toString.call(val) !== '[object Date]'
}

export function emptyTarget(val) {
    return Array.isArray(val) ? [] : {}
}

export function getEnumerableOwnPropertySymbols(target: any): any {
    return Object.getOwnPropertySymbols
      ? Object.getOwnPropertySymbols(target).filter(function(symbol) {
          return target.propertyIsEnumerable(symbol)
      })
      : []
}

function cloneUnlessOtherwiseSpecified(value, options: MergeOptions) {
    return (options.clone !== false && options.isMergeableObject(value))
      ? deepmerge(emptyTarget(value), value, options)
      : value;
}

function defaultArrayMerge(target, source, options: MergeOptions) {
    return target.concat(source).map(function(element) {
        return cloneUnlessOtherwiseSpecified(element, options)
    });
}

function getMergeFunction(key, options: MergeOptions) {
    if (!options.customMerge) {
        return deepmerge;
    }
    const customMerge = options.customMerge(key);
    return typeof customMerge === 'function' ? customMerge : deepmerge;
}

function getKeys(target) {
    return Object.keys(target).concat(getEnumerableOwnPropertySymbols(target));
}